import {
  Stack,
  Button,
  Title,
  Text,
  Group,
  Box,
  Skeleton,
} from '@mantine/core';
import BigNumber from 'bignumber.js';
import { useState, useEffect } from 'react';
import { LuArrowDownUp } from 'react-icons/lu';
import { FaCog } from 'react-icons/fa';
import { ContentCard } from 'src/components/ContentCard/ContentCard';
import {
  NumberInput,
  NumberInputProps,
} from 'src/components/NumberInput/NumberInput';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { Rows } from 'src/components/Rows/Rows';
import { Select, SelectProps } from 'src/components/Select/Select';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Token } from 'src/components/Token/Token';
import { SwapSettingsModal } from 'src/components/SwapSettingsModal/SwapSettingsModal';
import { SwapConfirmationModal } from 'src/components/SwapConfirmationModal/SwapConfirmationModal';
import { useTokensInfo } from 'src/api/dex';
import { usePrepareSwap } from 'src/hooks/usePrepareSwap';
import { formatDecimal, formatUnits } from 'src/utils';
import { NETWORK_VARS } from 'src/constants';
import { RouteIcon } from 'src/assets';
import { Quote, TradeType } from 'src/types';
import { TokensModal } from './TokensModal';
import { SwapRoutePlan } from './SwapRoutePlan';

const inputCommonProps: Partial<NumberInputProps> = {
  inputProps: {
    size: 'sm',
    labelProps: { w: '100%' },
    classNames: {
      root: 'bg-[#151519] rounded-[16px] border-none px-3 py-4',
      label: 'px-2 mb-3',
      wrapper: 'bg-transparent pb-[30px]',
      input:
        'border-none !px-2 bg-transparent text-[#A0A0B1] text-[24px] font-[700]',
      section: '!px-2',
    },
  },
  leftSectionProps: {
    className:
      'absolute left-0 bottom-0 top-auto h-[18px] justify-start align-end',
  },
  rightSectionProps: {
    className: 'h-[30px]',
  },
  rightSectionWidth: 'auto',
};

const tokenSelectCommonProps: Partial<SelectProps> = {
  valuePlaceholder: 'Select token',
  isTokenSelect: true,
  stackProps: { gap: 0 },
  comboboxProps: {
    styles: {
      dropdown: {
        minWidth: '200px',
      },
    },
  },
  targetProps: {
    px: 0,
    h: 30,
    bg: 'transparent',
    style: {
      border: 'none',
    },
  },
  labelProps: {
    fz: 20,
    fw: 700,
  },
};

export function SwapPanel() {
  const { tokensInfo, getTokenByAddress } = useTokensInfo();

  const [fromValue, setFromValue] = useState<string | undefined>();
  const [toValue, setToValue] = useState<string | undefined>();
  const [fromToken, setFromToken] = useState<string | undefined>(
    NETWORK_VARS.tokensAddresses.MON
  );
  const [toToken, setToToken] = useState<string | undefined>();
  const [selectingTokenType, setSelectingTokenType] = useState<
    'to' | 'from' | undefined
  >();
  const [tradeType, setTradeType] = useState<TradeType>('EXACT_INPUT');

  const [slippageValue, setSlippageValue] = useState<string>('auto');
  const [customSlippageValue, setCustomSlippageValue] = useState<
    string | undefined
  >();
  const [deadlineValue, setDeadlineValue] = useState<string | undefined>('20');

  const [settingsModalOpened, setSettingsModalOpened] = useState(false);

  const [quoteToApprove, setQuoteToApprove] = useState<Quote | undefined>();
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);
  const [confirmationModalError, setConfirmationModalError] = useState<
    Error | undefined
  >();

  const [routePlanOpened, setRoutePlanOpened] = useState(false);

  const fromTokenInfo = getTokenByAddress(fromToken);
  const toTokenInfo = getTokenByAddress(toToken);
  const inputTokenInfo =
    tradeType === 'EXACT_INPUT' ? fromTokenInfo : toTokenInfo;

  const _amount = tradeType === 'EXACT_INPUT' ? fromValue : toValue;
  const amountBN = BigNumber(_amount ?? 0);
  const amount = amountBN
    .times(inputTokenInfo?.decimals ? `1e${inputTokenInfo?.decimals}` : 1)
    .toFixed();

  const {
    quote,
    tokenInBalance,
    isLoadingQuote,
    quoteError,
    notEnoughBalanceForGas,
    transactionCost,
  } = usePrepareSwap({
    amount,
    tokenIn: fromToken,
    tokenOut: toToken,
    tradeType,
    slippage: slippageValue === 'custom' ? customSlippageValue : slippageValue,
    deadlineValue,
  });

  useEffect(() => {
    if (quote?.inAmount) {
      if (tradeType === 'EXACT_INPUT') {
        setToValue(formatUnits(quote.outAmount, toTokenInfo?.decimals));
      } else {
        setFromValue(formatUnits(quote.inAmount, fromTokenInfo?.decimals));
      }
    }
  }, [quote?.inAmount]);

  const handleFromValueChange = (newValue?: string) => {
    setFromValue(newValue);
    setTradeType('EXACT_INPUT');

    if (!BigNumber(newValue ?? 0).gt(0)) {
      setToValue(undefined);
    }
  };

  const handleToValueChange = (newValue?: string) => {
    setToValue(newValue);
    setTradeType('EXACT_OUTPUT');
  };

  const handleFromTokenChange = (newValue: string) => {
    if (toToken === newValue) {
      setToToken(fromToken);
    }

    setFromToken(newValue);
  };

  const handleToTokenChange = (newValue: string) => {
    if (fromToken === newValue) {
      setFromToken(toToken);
    }

    setToToken(newValue);
  };

  const handleSwapTokensInput = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setTradeType(tradeType === 'EXACT_INPUT' ? 'EXACT_OUTPUT' : 'EXACT_INPUT');

    if (tradeType === 'EXACT_INPUT') {
      setToValue(fromValue);
      setFromValue(undefined);
    } else {
      setFromValue(toValue);
      setToValue(undefined);
    }
  };

  const isLoading = isLoadingQuote;

  const isInsufficientBalance =
    typeof tokenInBalance !== 'undefined' &&
    BigNumber(amountBN).gt(tokenInBalance?.formatted || 0);

  const getSubmitErrorMessage = (): [
    submitErrorMessage: string,
    hideErrorMessage: boolean,
  ] => {
    if (!fromToken || !toToken) {
      return ['Select a token', true];
    }

    if (BigNumber(amountBN).lte(0)) {
      return ['Enter an amount', true];
    }

    if (notEnoughBalanceForGas) {
      return ['Insufficient balance to cover gas fees', false];
    }

    if (isInsufficientBalance) {
      return ['Insufficient balance', false];
    }

    return ['', false];
  };

  const [submitErrorMessage, hideErrorMessage] = getSubmitErrorMessage();
  const submitDisabled = !fromValue || !toValue || !!submitErrorMessage;

  const handleSubmit = () => {
    if (submitDisabled || isLoading) {
      return;
    }

    setQuoteToApprove(structuredClone(quote));
    setConfirmationModalOpened(true);
    setConfirmationModalError(undefined);
  };

  const tokenOptions =
    tokensInfo?.map((token) => ({
      value: token.contract_address,
      label: token.symbol,
    })) ?? [];

  const isSelectingToken = !!selectingTokenType;

  const handleCloseTokensModal = () => {
    setSelectingTokenType(undefined);
  };

  const renderSubmitLabel = () => {
    if (submitErrorMessage) {
      return submitErrorMessage;
    }

    return quote?.swapDetails?.calls?.find((c) => c.type === 'wrap')
      ? 'Wrap MON'
      : quote?.swapDetails?.calls?.find((c) => c.type === 'unwrap')
        ? 'Unwrap MON'
        : 'Swap';
  };

  const handleSwapConfirmationModalClose = () => {
    setConfirmationModalOpened(false);
    setQuoteToApprove(undefined);
  };

  const numOfHops =
    quote?.routePlan.reduce((acc, route) => acc + route.path.length, 0) ?? 0;

  const swapDetails = (
    <Stack gap={8}>
      <Rows
        withExpand
        items={[
          {
            label: 'Min. Received',
            value: quote?.outAmount ? (
              <>
                {formatDecimal(
                  formatUnits(quote.outAmount, toTokenInfo?.decimals),
                  { withTruncate: true }
                )}{' '}
                {toTokenInfo?.symbol}
              </>
            ) : (
              '-'
            ),
          },
          {
            label: 'Swap Rate',
            value: quote?.outAmount ? (
              <>
                1 {fromTokenInfo?.symbol} ={' '}
                {formatDecimal(
                  BigNumber(
                    formatUnits(quote?.outAmount || '1', toTokenInfo?.decimals)
                  ).div(
                    formatUnits(quote?.inAmount || '1', fromTokenInfo?.decimals)
                  ),
                  { withTruncate: true }
                )}{' '}
                {toTokenInfo?.symbol}
              </>
            ) : (
              '-'
            ),
          },
          {
            label: 'Protocol Fees',
            value: '0 MON',
          },
          {
            label: 'Network Fees',
            value: isLoadingQuote ? (
              <Skeleton w={160} h={23} />
            ) : transactionCost.eq(0) ? (
              '-'
            ) : (
              <>
                {formatDecimal(transactionCost, {
                  withTruncate: true,
                })}{' '}
                MON
              </>
            ),
          },
          {
            label: 'Best Route',
            value: (
              <Group
                component="span"
                gap={8}
                className="text-primarybrand cursor-pointer"
                onClick={() => setRoutePlanOpened(!routePlanOpened)}
              >
                <RouteIcon /> {numOfHops} Hop{numOfHops > 1 ? 's' : ''}
              </Group>
            ),
          },
          ...(routePlanOpened
            ? [
                {
                  label: '__route_plan__',
                  value: <SwapRoutePlan quote={quote} />,
                  showValueAsRow: true,
                },
              ]
            : []),
          {
            label: 'Max Slippage',
            value: quote?.slippageBps
              ? `${BigNumber(quote.slippageBps).div(100).toString()}%`
              : `${
                  slippageValue === 'auto'
                    ? 'dynamic'
                    : slippageValue === 'custom'
                      ? customSlippageValue
                      : slippageValue
                }%`,
          },
        ]}
      />
    </Stack>
  );

  return (
    <ContentCard
      withBgGradient={false}
      wrapperProps={{
        mt: 40,
        w: 480,
      }}
      cardProps={{
        p: 24,
        bg: '#0F1012',
        className:
          'shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] rounded-[32px] border-[#202027]',
      }}
    >
      <Group justify="space-between" mb={24}>
        <Title order={2} fz={24}>
          Swap
        </Title>
        <FaCog
          size={24}
          className="cursor-pointer"
          color="#7E7E95"
          onClick={() => setSettingsModalOpened(true)}
        />
      </Group>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Stack gap={24}>
          <Stack gap={6} className="relative">
            <NumberInput
              value={fromValue}
              label={
                <Group justify="space-between" w="100%">
                  <Text c="#7E7E95">Sell</Text>
                  <Token.Balance
                    tokenInfo={fromTokenInfo}
                    color={isInsufficientBalance ? '#FF3E55' : undefined}
                    onClick={setFromValue}
                  />
                </Group>
              }
              {...inputCommonProps}
              isInvalid={isInsufficientBalance}
              onChange={handleFromValueChange}
              onClick={
                fromToken ? undefined : () => setSelectingTokenType('from')
              }
              leftSection={
                <Token.Price tokenInfo={fromTokenInfo} amount={fromValue} />
              }
              rightSection={
                <Select
                  options={tokenOptions}
                  value={fromToken}
                  {...tokenSelectCommonProps}
                  onChange={handleFromTokenChange}
                  onClick={() => setSelectingTokenType('from')}
                />
              }
            />

            <Group
              w={40}
              h={40}
              className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] rounded-full bg-[#0F1012] cursor-pointer"
              onClick={handleSwapTokensInput}
            >
              <Box
                className="relative flex items-center justify-center rounded-full border border-solid border-[3px] border-primarybrand"
                w={28}
                h={28}
                mx="auto"
              >
                <div className="relative w-4 h-4">
                  <LuArrowDownUp
                    size={16}
                    className="absolute inset-0 text-primarybrand"
                  />
                </div>
              </Box>
            </Group>

            <NumberInput
              value={toValue}
              label={
                <Group justify="space-between" w="100%">
                  <Text c="#7E7E95">Buy</Text>
                  <Token.Balance tokenInfo={toTokenInfo} />
                </Group>
              }
              {...inputCommonProps}
              onChange={handleToValueChange}
              onClick={toToken ? undefined : () => setSelectingTokenType('to')}
              leftSection={
                <Token.Price tokenInfo={toTokenInfo} amount={toValue} />
              }
              rightSection={
                <Select
                  options={tokenOptions}
                  value={toToken}
                  {...tokenSelectCommonProps}
                  onClick={() => setSelectingTokenType('to')}
                  onChange={handleToTokenChange}
                />
              }
              readOnly
            />
          </Stack>

          <Stack gap={16}>
            <WalletSuspense buttonProps={{ size: 'lg' }}>
              <Button
                variant="gradient"
                size="lg"
                type="submit"
                disabled={submitDisabled}
                loading={isLoading}
              >
                {renderSubmitLabel()}
              </Button>
            </WalletSuspense>

            {!hideErrorMessage && (
              <ErrorDisplay>
                {quoteError?.message || confirmationModalError?.message}
              </ErrorDisplay>
            )}

            {quote && swapDetails}
          </Stack>
        </Stack>
      </form>

      {settingsModalOpened && (
        <SwapSettingsModal
          slippageValue={slippageValue}
          customSlippageValue={customSlippageValue}
          deadlineValue={deadlineValue}
          onSlippageChange={(newValue: string) => {
            setSlippageValue(newValue);
            if (newValue !== 'custom') {
              setCustomSlippageValue(undefined);
            }
          }}
          onCustomSlippageChange={setCustomSlippageValue}
          onDeadlineChange={setDeadlineValue}
          onClose={() => setSettingsModalOpened(false)}
        />
      )}

      <TokensModal
        opened={isSelectingToken}
        selectedToken={selectingTokenType === 'from' ? fromToken : toToken}
        onSelectToken={(newTokenValue: string) => {
          if (selectingTokenType === 'from') {
            handleFromTokenChange(newTokenValue);
          } else {
            handleToTokenChange(newTokenValue);
          }

          handleCloseTokensModal();
        }}
        onClose={handleCloseTokensModal}
      />

      {confirmationModalOpened && (
        <SwapConfirmationModal
          fromValue={fromValue!}
          toValue={toValue!}
          fromToken={fromToken!}
          toToken={toToken!}
          quote={quoteToApprove!}
          onClose={handleSwapConfirmationModalClose}
          onSuccess={() => {
            setFromValue(undefined);
            setToValue(undefined);
            handleSwapConfirmationModalClose();
          }}
          onError={(err) => {
            setConfirmationModalError(err);
            handleSwapConfirmationModalClose();
          }}
        />
      )}
    </ContentCard>
  );
}
